import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CreateWatermarkBodyDto, UpdateWatermarkBodyDto, WatermarkDto } from '@picflow/types';
import { map, switchMap, tap } from 'rxjs';
import { AppConfigService } from '../providers/config.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { TenantService } from './tenant.service';

@Injectable({
  providedIn: 'root',
})
export class WatermarkService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(ObservableDataLoaderService);
  private tenantService = inject(TenantService);

  watermarks$ = this.tenantService.activeTenant$.pipe(
    switchMap(tenant => {
      if (!tenant) return;
      return this.loader.get(
        'watermarks',
        () =>
          this.http.get<{ data: WatermarkDto[] }>(`${this.config.apiEndpoint}/watermarks`).pipe(map(resp => resp.data)),
        1000 * 60 * 5
      );
    })
  );

  public getWatermark(watermarkId: string) {
    return this.watermarks$.pipe(map(watermarks => watermarks.find(watermark => watermark['id'] === watermarkId)));
  }

  public createWatermark(params: CreateWatermarkBodyDto) {
    return this.http.post<WatermarkDto>(`${this.config.apiEndpoint}/watermarks`, params).pipe(
      tap(watermark => {
        this.loader.update<WatermarkDto[]>('watermarks', watermarks => {
          if (!watermarks) return;
          if (!watermarks.some(w => w['id'] === watermark['id'])) {
            watermarks.push(watermark);
          }
          return watermarks;
        });
      })
    );
  }

  public deleteWatermark(watermarkId: string) {
    return this.http.delete(`${this.config.apiEndpoint}/watermarks/${watermarkId}`).pipe(
      tap(() => {
        this.loader.update<WatermarkDto[]>('watermarks', watermarks => {
          if (!watermarks) return;
          const index = watermarks.findIndex(watermark => watermark['id'] === watermarkId);
          if (index !== -1) watermarks.splice(index, 1);
          return watermarks;
        });
      })
    );
  }

  public updateWatermark(watermarkId: string, params: UpdateWatermarkBodyDto) {
    return this.http.patch<WatermarkDto>(`${this.config.apiEndpoint}/watermarks/${watermarkId}`, params).pipe(
      tap(watermark => {
        this.loader.update<WatermarkDto[]>('watermarks', watermarks => {
          if (!watermarks) return;
          const index = watermarks.findIndex(watermark => watermark['id'] === watermarkId);
          if (index !== -1) watermarks[index] = watermark;
          return watermarks;
        });
      })
    );
  }
}
